import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  TextField,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import PersistentDataGrid from "../../components/table/PersistentDataGrid";
import { GridToolbar } from "@mui/x-data-grid";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons/faEllipsisV";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import InventoryServices from "../../services/InventoryServices";
import { useSelector } from "react-redux";
import translations from "../../assets/locals/translations";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ModalForm from "./ModalForm";
import StepForm from "./StepForm";
import { useDispatch } from "react-redux";
import { setShowMessage } from "../../redux/slices/notificationSlice";

const DeliveryStepsData = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const stepKeyFromState = location.state?.stepKey || null;
  const params = useParams();
  const stepKeyParam = params?.type || null;
  const stepKey = stepKeyFromState || stepKeyParam;
  const InventoryService = new InventoryServices();
  const currentLanguage = useSelector((state) => state.language.language);
  const t = translations[currentLanguage] || translations["en"];
  const [stockData, setStockData] = useState([]);
  const [loading, setLoading] = useState(false);
  const pageSizeKey = `rowsPerPage_${window.location.pathname}`;
  const [pageSize, setPageSize] = useState(() => {
    const saved = sessionStorage.getItem(pageSizeKey);
    return saved ? parseInt(saved, 10) : 50;
  });
  const [allStepsData, setAllStepsData] = useState({});
  const [stepIndex, setStepIndex] = useState(null);
  const [stepTitle, setStepTitle] = useState("");

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState({ modal: false, data: null });

  useEffect(() => {
    // Update stepIndex/title when navigation state is present
    if (location.state) {
      setStepIndex(location.state?.stepIndex ?? null);
      setStepTitle(location.state?.stepTitle ?? "");
      return;
    }

    // Fallback: derive a localized step title from the route param when state isn't provided
    if (stepKeyParam) {
      const mapTypeToTitleKey = {
        chassis_print: "chassisPrintTitle",
        documents: "documentsTitle",
        parsing_complete: "parsingPendingTitle",
        plate_order: "plateOrderTitle",
        rc_fitment: "rcFitmentTitle",
        rc_dispatch: "rcDispatchTitle",
        delivery: "delivery",
      };

      const titleKey = mapTypeToTitleKey[stepKeyParam] || stepKeyParam;
      setStepIndex(null);
      setStepTitle(t[titleKey] || titleKey);
      return;
    }

    // If nothing found, clear title/index
    setStepIndex(null);
    setStepTitle("");
  }, [location.state, stepKeyParam, currentLanguage, t]);

  const editEnquiryCell = (row) => {
    navigate(`/delivery/steps/finalization/edit`, {
      state: { data: row },
    });
  };

  // --- START: COLUMNS DEFINITION (NO CHANGE HERE) ---
  const [columns, setColumns] = useState([
    {
      field: "menu",
      headerName: t.menu || "Menu",
      className: "bg-dark",
      sortable: false,
      filterable: false,
      headerAlign: "left",
      align: "left",
      disableColumnMenu: true,
      width: 50,
      renderCell: (params) => (
        <>
          {stepKey === "delivery" ? (
            <Tooltip title={t.edit || "Edit"}>
              <button
                className="myActionBtn m-1"
                onClick={() => editEnquiryCell(params.row)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  className="bi bi-pencil-square"
                  viewBox="0 0 16 16"
                >
                  <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
                  <path
                    fillRule="evenodd"
                    d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"
                  />
                </svg>
              </button>
            </Tooltip>
          ) : (
            <Tooltip title={t.addenq || "Add Enquiry"}>
              <button
                onClick={() => handleOpenModal(params.row)}
                className="myActionBtn d-flex align-items-center"
                type="button"
                style={{ cursor: "pointer" }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="22"
                  height="22"
                  fill="currentColor"
                  className="bi bi-plus-circle text-primary"
                  viewBox="0 0 16 16"
                >
                  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                  <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
                </svg>
              </button>
            </Tooltip>
          )}
        </>
      ),
    },
    { field: "bil", headerName: t.bil || "BIL", flex: 1, minWidth: 80 },
    { field: "bil_no", headerName: t.bilNo || "BIL NO", flex: 1, minWidth: 150 },
    {
      field: "date",
      headerName: (t.date || "Date").toUpperCase(),
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => {
        if (!params.value) return ""; // handles null, undefined, or empty string
        const date = moment(params.value);
        return date.isValid() ? date.format("DD-MM-YYYY") : "";
      },
    },
    { field: "bum_cod", headerName: t.bumCod || "BUM COD", flex: 1, minWidth: 100 },
    { field: "model_name", headerName: t.modal || t.model || "MODAL", flex: 1, minWidth: 100 },
    { field: "chassis", headerName: t.chassis || "CHASSIS", flex: 1, minWidth: 200 },
    { field: "engine", headerName: t.engine || "ENGINE", flex: 1, minWidth: 200 },
    {
      field: "delivery_date",
      headerName: (t.deliveryDate || "Delivery Date").toUpperCase(),
      flex: 1,
      minWidth: 180,
      valueFormatter: (params) => {
        if (!params.value) return "";
        const date = moment(params.value);
        return date.isValid() ? date.format("DD-MM-YYYY") : "";
      },
    },
    {
      field: "phone_number",
      headerName: (t.phoneno || "Phone Number").toUpperCase(),
      flex: 1,
      minWidth: 180,
    },
    {
      field: "customer_name",
      headerName: (t.customerName || "Customer Name").toUpperCase(),
      flex: 1,
      minWidth: 310,
    },
    {
      field: "village",
      headerName: (t.village || "Village").toUpperCase(),
      flex: 1,
      minWidth: 200,
    },
    {
      field: "taluka",
      headerName: (t.taluka || "Taluka").toUpperCase(),
      flex: 1,
      minWidth: 150,
    },
  ]);
  // --- END: COLUMNS DEFINITION ---

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchQuery, setSearchQuery] = useState("");

  // --- Other functions (getStepsColumns, getDeliveryStepsLogs, etc. remain the same) ---
  const getStepsColumns = (stepKey) => {
    const formatHeaderName = (field) => {
      return field.replace(/_/g, " ").toUpperCase(); // If you want ALL CAPS
    };
    const baseConfig = (field) => {
      let customHeader = formatHeaderName(field);

      if (stepKey === "rc_dispatch" && field === "registration_no") {
        customHeader = t.rcNumberLabel || "RC NUMBER";
      }

      if (stepKey === "rc_dispatch" && field === "fitting_date") {
        return {
          field: field,
          headerName: t.dispatchedDate || "DISPATCHED DATE",
          minWidth: 200,
          flex: 1,
          headerAlign: "left",
          align: "left",
          valueFormatter: (params) => {
            if (!params.value) return "";
            const date = moment(params.value);
            return date.isValid() ? date.format("DD-MM-YYYY") : "";
          },
        };
      }

      return {
        field: field,
        headerName: customHeader,
        minWidth: 200,
        flex: 1,
        headerAlign: "left",
        align: "left",

        // ✅ NEW CONDITIONAL FORMATTERS:
        valueFormatter:
          stepKey === "rc_dispatch" && field === "registration_no"
            ? (params) => {
              const val = String(params.value || "").toUpperCase();
              return val === "YES" || val === "NO" ? "" : val;
            }
            : stepKey === "rc_dispatch" && field === "dispatch_status"
              ? (params) => {
                const val = String(params.value || "").toLowerCase();
                return val === "dispatched" ? (t.dispatched || "Dispatched") : (t.stock || "Stock");
              }
              : undefined,
      };
    };




    let fields = [];

    switch (stepKey) {
      case "chassis_print":
        fields = ["chassis_print", "remark"];
        break;

      case "documents":
      case "parse_documents":
        fields = ["aadhar_card", "jamin_no", "bank_passbook", "remark"];
        break;

      case "parsing_complete":
        fields = [
          "retail_bill_no",
          "insurance",
          "tax",
          "penalty",
          "total",
          "registration_no",
          "remark",
        ];
        break;
      case "parsing_type":
        fields = ["parsing_type", "remark"];
        break;

      case "subsidy":
        fields = ["subsidy", "remark"];
        break;

      case "plate_order":
        fields = ["no_plate_ordered", "remark"];
        break;

      case "rc_dispatch":
        fields = ["registration_no", "dispatch_status", "fitting_date", "remark"]; // Ensure dispatch_status is here
        break;

      case "rc_fitment":
        fields = ["rc_fitment", "remark"];
        break;



      default:
        return [];
    }

    return fields.map(baseConfig);
  };

  const getDeliveryStepsLogs = async (step) => {
    setLoading(true);
    try {
      const response = await InventoryService.getDeliveryStepsLogs(step);
      if (response && response.isSuccess) {
        const rowData = response.result.map((item, index) => ({
          ...item,
          bil: index + 1,
        }));
        setStockData(rowData);
        const stepsColumns = getStepsColumns(stepKey);
        setColumns((prevCol) => {
          const existingKeys = new Set(prevCol.map((col) => col.field));
          const newCols = stepsColumns.filter(
            (col) => !existingKeys.has(col.field)
          );

          if (newCols.length === 0) return prevCol;

          // This logic to insert columns seems a bit complex, ensure it works as expected.
          // A simpler approach might be to rebuild the column array from scratch each time.
          // However, keeping your existing logic:
          const baseCols = [
            // ... your base columns
          ];
          return [
            ...prevCol.slice(0, 1), // Keep menu
            ...prevCol.slice(1, -1).filter(c => !stepsColumns.find(sc => sc.field === c.field)), // remove old step columns
            ...stepsColumns, // add new step columns
            ...prevCol.slice(-1) // add last base column
          ];
        });
      }
    } catch (error) {
      console.error(error, "Error getting stocks list");
    } finally {
      setLoading(false);
    }
  };

  // Functions like handleOpenModal, handleCloseModal, handleSaveStepData remain unchanged
  const handleOpenModal = (row) => { setIsModalOpen({ modal: true, data: row }); };
  const handleCloseModal = () => { setIsModalOpen({ modal: false, data: null }); };
  const handleSaveStepData = async (index, values) => {
    console.log(`Saving values for step ${index}:`, values, isModalOpen.data);
    try {
      if (stepKey === "number22") {
        // 22 Number step: update only number22 on stock log (delivery step update)
        const row = isModalOpen.data;
        const payload = {
          id: row.id,
          number22: values.number22 ?? "",
        };
        const response = await InventoryService.updateStockLogNumber22(payload);
        if (response && response.isSuccess) {
          dispatch(setShowMessage(response.message || "Operation successful!"));
          await getDeliveryStepsLogs(stepKey);
          handleCloseModal();
        }
        return;
      }
      const payload = {
        ...values,
        deliveryId: isModalOpen.data.deliveryId,
        stepKey,
      };
      const response = await InventoryService.storeAdditionalLog(payload);
      if (response && response.isSuccess) {
        dispatch(setShowMessage(response.message || "Operation successful!"));
        await getDeliveryStepsLogs(stepKey);
        handleCloseModal();
      }
    } catch (error) {
      console.error("Submission Error:", error);
    }
  };

  useEffect(() => {
    if (!stepKey) {
      return;
    }
    getDeliveryStepsLogs(stepKey);
  }, [stepKey]);

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    try { sessionStorage.setItem(pageSizeKey, String(newPageSize)); } catch (e) { }
  };

  const filteredRows = useMemo(() => {
    if (!searchQuery) return stockData;
    return stockData.filter((row) =>
      Object.keys(row).some((field) =>
        String(row[field]).toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [searchQuery, stockData]);

  // Hide delivery date, phone number, customer name, village, taluka when 22nd card (number22) is selected
  const displayColumns = useMemo(() => {
    if (stepKey === "number22") {
      const hideFor22 = [
        "delivery_date",
        "phone_number",
        "customer_name",
        "village",
        "taluka",
      ];
      return columns.filter((col) => !hideFor22.includes(col.field));
    }
    return columns;
  }, [stepKey, columns]);

  return (
    <>
      {/* 
        NEW: Add this Box component to inject CSS styles for the colored rows.
        This is a common MUI pattern for component-specific styling.
      */}
      <Box
        sx={{
          width: "100%",
          "& .row-dispatched": {
            backgroundColor: "#e8f5e9", // Light green color
            "&:hover": {
              backgroundColor: "#c8e6c9 !important", // Slightly darker green on hover
            },
          },
        }}
      >
        <div className="bg-white p-2">
          {/* Header with title and global search input */}
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "stretch" : "center",
              justifyContent: "flex-start",
              mb: 1,
            }}
          >
            <TextField
              variant="outlined"
              placeholder={t.globalSearch || "Global Search..."}
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ width: isMobile ? "100%" : 300 }}
            />
          </Box>

          <div
            className="tableMenuHover"
            style={{ flexGrow: 1, width: "100%", height: "calc(100vh - 70px)" }}
          >
            <PersistentDataGrid
              rows={filteredRows}
              columns={displayColumns}
              getRowId={(row) => row.bil}
              pagination
              pageSize={pageSize}
              pageSizeOptions={[10, 15, 25, 50]}
              initialState={{
                pagination: { paginationModel: { pageSize: 50 } },
              }}
              rowsPerPageOptions={[10, 20, 25, 50, 100]}
              onPageSizeChange={handlePageSizeChange}
              disableSelectionOnClick
              autoHeight={isMobile}
              //
              // NEW: Add the getRowClassName prop here
              //
              getRowClassName={(params) => {
                // Check if the current view is 'rc_dispatch' and the status is 'Dispatched'
                if (
                  stepKey === "rc_dispatch" &&
                  params.row.dispatch_status === "Dispatched"
                ) {
                  return "row-dispatched";
                }
                return ""; // Return empty string for default styling
              }}
              components={{
                Toolbar: GridToolbar,
                NoRowsOverlay: () => (
                  <div
                    style={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                    }}
                  >
                    <span style={{ fontFamily: "Poppins" }}>
                      {t.noDataAvailable || "No Data Available"}
                    </span>
                  </div>
                ),
              }}
              componentsProps={{
                toolbar: {
                  position: "right",
                  style: { fontFamily: "Poppins", alignSelf: "end" },
                },
              }}
              sx={{ width: "100%" }}
              loading={loading}
            />
          </div>
        </div>
      </Box>

      {/* --- Modal Rendering (No change here) --- */}
      {stepIndex !== null && (
        <ModalForm
          isOpen={isModalOpen.modal}
          onClose={handleCloseModal}
          title={stepTitle}
        >
          <StepForm
            key={stepKey === "number22" && isModalOpen.data ? isModalOpen.data.id : stepIndex}
            stepIndex={stepIndex}
            stepTitle={t.addRecord || `Add Record`}
            initialData={
              stepKey === "number22" && isModalOpen.data
                ? { number22: isModalOpen.data.number22 ?? "" }
                : allStepsData[stepIndex]
            }
            onSave={handleSaveStepData}
          />
        </ModalForm>
      )}
    </>
  );
};

export default DeliveryStepsData;